// Custom React hook to manage the contact form logic, validation, and EmailJS integration
import { useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { trackFormSubmission, trackEvent } from '../utils/analytics';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { contactFormCopy } from '@/contexts/language/translations/pages/contactForm';

// Human check: 7 + 1 = 8 (validated on submit, not sent to backend)
const HUMAN_CHECK_ANSWER = 8;

// Define the schema for form validation using Zod (messages in the visitor's language)
const buildFormSchema = (c: typeof contactFormCopy.en) => z.object({
  name: z.string().min(2, { message: c.nameError }),
  email: z.string().email({ message: c.emailError }),
  company: z.string().optional(),
  companySize: z.string().optional(),
  message: z.string().optional(),
  humanCheck: z.union([z.undefined(), z.number()]).refine((v) => v === HUMAN_CHECK_ANSWER, { message: c.humanCheckError }),
});

export type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

export const useContactForm = (service: string) => {
  const { language } = useLanguage();
  const c = contactFormCopy[language];
  const formSchema = useMemo(() => buildFormSchema(c), [c]);
  // State for form submission and success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  
  // Initialize react-hook-form with Zod validation and default values
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      companySize: '',
      message: '',
      humanCheck: 0,
    }
  });

  // Reset the form
  const resetForm = () => {
    setIsSuccess(false);
    form.reset();
  };

  // Handle form submission and send email via Cloud Run backend
  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    let httpStatus: number | null = null;
    try {
      // Prepare payload for Cloud Run (humanCheck is not sent)
      const payload = {
        name: data.name,
        email: data.email,
        company: data.company || 'Not specified',
        company_size: data.companySize || 'Not specified',
        message: data.message || 'Not specified',
        phone_number: ''
      };
      const CLOUD_RUN_URL = "/api/contact";

      const response = await fetch(CLOUD_RUN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      httpStatus = response.status;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      setIsSuccess(true);
      toast.success(c.toastSuccess);
      form.reset();
      
      // Track successful form submission (engagement event).
      trackFormSubmission('contact_form');

      // Conversion event for GA4, sent via GTM. A GTM Custom Event trigger
      // ("contact_form_success") fires the GA4 Event tag on this single push.
      // IMPORTANT: do NOT also call gtag('event','contact_form_success') here —
      // GTM's Custom Event trigger matches that command too, so the tag would
      // fire twice and double-count the conversion (verified in GTM Preview).
      // This dataLayer.push must remain the SOLE source of the conversion.
      // Wired in GTM-54XNMV9 → GA4 G-ZTJ90PZBG5.
      if (typeof window !== 'undefined') {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ event: 'contact_form_success', form_name: 'contact_form' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      let errorMessage = c.toastError;
      if (error instanceof Error) {
        errorMessage += `${c.toastErrorDetail}${error.message}`;
      }
      toast.error(errorMessage);

      // Track form submission error — with full payload for diagnostics
      const errorType = error instanceof Error ? error.name : 'UnknownError';
      const errorMsg  = error instanceof Error ? error.message : String(error);
      trackEvent('contact_form_error', 'error', 'form_submission_failed');
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'contact_form_error', {
          error_type:    errorType,
          error_message: errorMsg,
          status_code:   httpStatus,
          page_location: window.location.href,
          page_hostname: window.location.hostname,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return all state and handlers needed by the ContactForm component
  return {
    form,
    formRef,
    isSubmitting,
    isSuccess,
    setIsSuccess,
    onSubmit,
    resetForm
  };
};
